// Browse — domain tab strip, faceted filters, sticky toolbar, virtualized grid,
// bulk action bar, record drawer.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { RecordRow, RecordStatus } from "@/lib/types";
import { useBrowseStore } from "@/store/browse";
import { TextField } from "@/components/primitives/TextField";
import { Button } from "@/components/primitives/Button";
import { StatusChip } from "@/components/primitives/Chip";
import { DataGrid, type SortState, type DataGridColumn } from "@/components/grid/DataGrid";
import { Filters, type Facet } from "@/components/grid/Filters";
import { ColumnPicker } from "@/components/grid/ColumnPicker";
import { DensityToggle } from "@/components/grid/DensityToggle";
import { SavedViews } from "@/components/grid/SavedViews";
import { ExportMenu } from "@/components/grid/ExportMenu";
import { Drawer } from "@/components/primitives/Drawer";
import { Icon } from "@/lib/icons";
import { fmtRelative } from "@/lib/formatters";
import { useToast } from "@/components/primitives/Toast";

interface DomainTab { id: string; label: string; count: string }

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return n.toLocaleString();
}

const STATUS_FACET: Facet = {
  key: "status",
  label: "Status",
  options: [
    { value: "live", label: "Live", count: 1180 },
    { value: "pending", label: "Pending", count: 12 },
    { value: "draft", label: "Draft", count: 6 },
    { value: "conflict", label: "Conflict", count: 3 },
    { value: "deprecated", label: "Deprecated", count: 28 },
  ],
};

const REGION_FACET: Facet = {
  key: "region",
  label: "Region",
  options: [
    { value: "NA", label: "North America", count: 612 },
    { value: "EU", label: "Europe", count: 384 },
    { value: "APAC", label: "Asia Pacific", count: 198 },
    { value: "LATAM", label: "Latin America", count: 42 },
  ],
};

const OWNER_FACET: Facet = {
  key: "owner",
  label: "Owner",
  options: [
    { value: "olivia", label: "Olivia Park" },
    { value: "daniel", label: "Daniel Wu" },
    { value: "mira", label: "Mira Costa" },
    { value: "sam", label: "Sam Reilly" },
  ],
};

const UPDATED_FACET: Facet = {
  key: "updated",
  label: "Last updated",
  options: [
    { value: "1d", label: "Last 24 hours" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
  ],
};

function mockRecords(domain: string, n = 60): RecordRow[] {
  const statuses: RecordStatus[] = ["live", "live", "live", "pending", "draft", "conflict"];
  const owners = ["Olivia Park", "Daniel Wu", "Mira Costa", "Sam Reilly"];
  const out: RecordRow[] = [];
  const now = Date.now();
  for (let i = 0; i < n; i++) {
    const code = `${domain.slice(0, 3).toUpperCase()}-${(1000 + i).toString()}`;
    out.push({
      id: code,
      domainId: domain,
      version: 1 + (i % 8),
      status: statuses[i % statuses.length],
      data: {
        code,
        name: `${domain.charAt(0).toUpperCase() + domain.slice(1, -1)} ${i + 1}`,
        region: ["NA", "EU", "APAC", "LATAM"][i % 4],
      },
      createdAt: new Date(now - i * 86400000 * 2).toISOString(),
      createdBy: owners[i % owners.length],
      updatedAt: new Date(now - i * 3600000).toISOString(),
      updatedBy: owners[i % owners.length],
      qualityScore: 80 + (i * 17) % 20,
      tags: i % 3 === 0 ? ["primary"] : [],
    });
  }
  return out;
}

export function Browse() {
  const { domain = "instruments" } = useParams();
  const navigate = useNavigate();
  const browse = useBrowseStore();
  const state = browse.byDomain[domain] ?? { q: "", filters: {}, page: 0, pageSize: 50, visibleColumns: [], views: [] };
  const toast = useToast();

  const [rows, setRows] = useState<RecordRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<SortState | null>({ key: "updatedAt", dir: "desc" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerRow, setDrawerRow] = useState<RecordRow | null>(null);

  // Live domain tabs from the API
  const [domains, setDomains] = useState<DomainTab[]>([]);
  const refreshDomains = () => api.domains.list()
    .then(list => setDomains(list.map(d => ({ id: d.id, label: d.name, count: fmtCount(d.recordCount ?? 0) }))))
    .catch(() => { /* leave previous list */ });
  useEffect(() => {
    refreshDomains();
    const onMutation = () => refreshDomains();
    window.addEventListener("aidan:mutation", onMutation);
    return () => window.removeEventListener("aidan:mutation", onMutation);
  }, []);


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.records.list(domain, { q: state.q, pageSize: 200 })
      .then(res => { if (!cancelled) setRows(res.items); })
      .catch(() => { if (!cancelled) setRows(mockRecords(domain, 120)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [domain, state.q]);

  const columns: DataGridColumn<RecordRow>[] = useMemo(() => [
    {
      key: "code",
      label: "Code",
      width: 160,
      sortable: true,
      render: (r) => <span className="lex-cell-strong" style={{ fontFamily: "var(--font-mono)" }}>{String(r.data.code ?? r.id)}</span>,
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (r) => <span>{String(r.data.name ?? "—")}</span>,
    },
    {
      key: "status",
      label: "Status",
      width: 130,
      sortable: true,
      render: (r) => <StatusChip status={r.status} />,
    },
    {
      key: "region",
      label: "Region",
      width: 100,
      sortable: true,
      render: (r) => <span className="lex-cell-muted">{String(r.data.region ?? "—")}</span>,
    },
    {
      key: "version",
      label: "v",
      width: 60,
      align: "right",
      sortable: true,
      render: (r) => <span className="lex-cell-muted">{r.version}</span>,
    },
    {
      key: "quality",
      label: "Quality",
      width: 140,
      align: "right",
      sortable: true,
      render: (r) => (
        <span className="lex-cell-bar">
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{r.qualityScore?.toFixed(0) ?? "—"}</span>
          <span className="lex-cell-bar__track">
            <span
              className="lex-cell-bar__fill"
              data-tone={r.qualityScore && r.qualityScore < 85 ? "warn" : undefined}
              style={{ width: `${r.qualityScore ?? 0}%` }}
            />
          </span>
        </span>
      ),
    },
    {
      key: "updatedAt",
      label: "Updated",
      width: 140,
      align: "right",
      sortable: true,
      render: (r) => <span className="lex-cell-muted">{fmtRelative(r.updatedAt)}</span>,
    },
    {
      key: "updatedBy",
      label: "By",
      width: 130,
      render: (r) => <span className="lex-cell-muted">{r.updatedBy}</span>,
    },
  ], []);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const out = [...rows];
    out.sort((a, b) => {
      const av = sort.key === "code" ? String(a.data.code ?? "") :
                 sort.key === "name" ? String(a.data.name ?? "") :
                 sort.key === "region" ? String(a.data.region ?? "") :
                 sort.key === "status" ? a.status :
                 sort.key === "version" ? a.version :
                 sort.key === "quality" ? (a.qualityScore ?? 0) :
                 sort.key === "updatedAt" ? a.updatedAt :
                 String(a[sort.key as keyof RecordRow] ?? "");
      const bv = sort.key === "code" ? String(b.data.code ?? "") :
                 sort.key === "name" ? String(b.data.name ?? "") :
                 sort.key === "region" ? String(b.data.region ?? "") :
                 sort.key === "status" ? b.status :
                 sort.key === "version" ? b.version :
                 sort.key === "quality" ? (b.qualityScore ?? 0) :
                 sort.key === "updatedAt" ? b.updatedAt :
                 String(b[sort.key as keyof RecordRow] ?? "");
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, sort]);

  const filteredRows = useMemo(() => {
    const f = state.filters;
    if (!Object.keys(f).length) return sortedRows;
    return sortedRows.filter(r => {
      if (f.status && r.status !== f.status) return false;
      if (f.region && r.data.region !== f.region) return false;
      return true;
    });
  }, [sortedRows, state.filters]);

  const activeTabId = domains.find(d => d.id === domain)?.id ?? domain;
  const activeLabel = domains.find(d => d.id === domain)?.label ?? domain;

  return (
    <div className="lex-page">
      <header className="lex-browse__head">
        <span className="lex-eyebrow">REFERENCE DATA</span>
        <h1 className="lex-display" style={{ fontSize: "clamp(24px, 3vw, 34px)" }}>
          Browse <span className="lex-display__em">{activeLabel.toLowerCase()}</span>
        </h1>
        <nav className="lex-browse__tabs" aria-label="Domains">
          {domains.map(d => (
            <Link
              key={d.id}
              to={`/browse/${d.id}`}
              className={`lex-browse__tab ${d.id === activeTabId ? "lex-browse__tab--active" : ""}`}
              aria-current={d.id === activeTabId ? "page" : undefined}
            >
              <span>{d.label}</span>
              <span className="lex-browse__tab-count">{d.count}</span>
            </Link>
          ))}
        </nav>
      </header>

      <div className="lex-browse__body">
        <Filters
          facets={[STATUS_FACET, REGION_FACET, OWNER_FACET, UPDATED_FACET]}
          values={state.filters}
          onChange={(k, v) => browse.setFilter(domain, k, v)}
          onClear={() => browse.clearFilters(domain)}
        />

        <div className="lex-browse__main">
          <div className="lex-toolbar">
            <TextField
              label="Search"
              placeholder={`Search ${activeTabId}…`}
              value={state.q}
              onChange={(e) => browse.setQuery(domain, e.target.value)}
              leadingIcon={<Icon.Search size={16} />}
              size="sm"
            />
            <div className="lex-toolbar__right">
              <SavedViews
                views={state.views}
                current={state.activeViewId}
                onApply={(id) => browse.applyView(domain, id)}
                onSave={(name) => browse.saveView(domain, name)}
                onDelete={(id) => browse.deleteView(domain, id)}
              />
              <ColumnPicker
                columns={columns.map(c => ({ key: c.key, label: c.label }))}
                visible={state.visibleColumns}
                onChange={(cols) => browse.setColumns(domain, cols)}
              />
              <DensityToggle />
              <ExportMenu onExport={(fmt) => toast(`Exporting ${filteredRows.length} records as ${fmt.toUpperCase()}…`, "info")} />
              <Button leadingIcon={<Icon.Plus size={16} />} size="sm" onClick={() => toast(`New ${domain.replace(/s$/, "")} record — draft created`, "ok")}>New record</Button>
            </div>
          </div>

          <DataGrid<RecordRow>
            rows={filteredRows}
            columns={columns.filter(c => state.visibleColumns.length === 0 || state.visibleColumns.includes(c.key))}
            rowKey={(r) => r.id}
            sort={sort}
            onSort={setSort}
            selected={selected}
            onSelectChange={setSelected}
            onRowActivate={(r) => setDrawerRow(r)}
            loading={loading}
            empty="No records match your filters."
            zebra
          />

          {selected.size > 0 ? (
            <div style={{ position: "sticky", bottom: 16, alignSelf: "center" }}>
              <div className="lex-bulkbar">
                <span>{selected.size} selected</span>
                <span className="lex-bulkbar__spacer" />
                <Button size="sm" variant="tonal" tone="secondary" onClick={() => { toast(`Submitted ${selected.size} records for approval`, "ok"); setSelected(new Set()); }}>Approve</Button>
                <Button size="sm" variant="tonal" tone="neutral" onClick={() => toast(`Exporting ${selected.size} selected records…`, "info")}>Export</Button>
                <Button size="sm" variant="text" tone="neutral" onClick={() => setSelected(new Set())}>Clear</Button>
              </div>
            </div>
          ) : null}

          <div className="lex-browse__foot lex-cell-muted">
            Showing {filteredRows.length} of {rows.length} {domain}
          </div>
        </div>
      </div>

      <Drawer
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        title={drawerRow ? String(drawerRow.data.name ?? drawerRow.id) : "Record"}
      >
        {drawerRow ? (
          <div className="lex-record-drawer">
            <div className="lex-record-drawer__head">
              <StatusChip status={drawerRow.status} />
              <span className="lex-chip" data-size="sm">v{drawerRow.version}</span>
              <span className="lex-chip" data-size="sm">Updated {fmtRelative(drawerRow.updatedAt)}</span>
            </div>
            <dl className="lex-record-drawer__fields">
              {Object.entries(drawerRow.data).map(([k, v]) => (
                <div key={k} className="lex-record-drawer__row">
                  <dt>{k}</dt>
                  <dd>{String(v ?? "—")}</dd>
                </div>
              ))}
              <div className="lex-record-drawer__row"><dt>Updated by</dt><dd>{drawerRow.updatedBy}</dd></div>
              <div className="lex-record-drawer__row"><dt>Created</dt><dd>{fmtRelative(drawerRow.createdAt)}</dd></div>
              <div className="lex-record-drawer__row"><dt>Quality</dt><dd>{drawerRow.qualityScore ?? "—"}%</dd></div>
            </dl>
            <div className="lex-record-drawer__cta">
              <Button onClick={() => navigate(`/browse/${domain}/${drawerRow.id}`)}>Open record</Button>
              <Button variant="outlined" tone="neutral" onClick={() => navigate(`/versions/${domain}/${drawerRow.id}`)}>Versions</Button>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

export default Browse;
